import type { useLazyGetAccountHolderQuery } from "@/modules/account/services";
import {
  AttachmentNetwork,
  AttachmentType,
  BeneficiaryAttachmentKeys
} from "@/modules/beneficiary/common/enum";
import type { IAttachmentInfo } from "@/modules/beneficiary/common/types";
import type { useAttachmentValidationMutation } from "@/modules/beneficiary/services";
import type { useLazyGetCardHolderQuery } from "@/modules/card/services";
import { BeneficiaryType, ErrorMessages, SnackbarAlertTypes } from "@/shared/common/enums";
import type { IError } from "@/shared/common/types";
import {
  getErrorMessage,
  isValidAccount,
  isValidAccountChecksum,
  isValidCard,
  trimAll
} from "@/shared/helpers";
import { useAppDispatch, useNotify } from "@/shared/hooks";
import { setAlertNotification } from "@/shared/state/notification";
import { debounce } from "@/shared/utils";
import { FormField } from "@ab.uitools/ui-library/components/FormField";
import { Input } from "@ab.uitools/ui-library/components/Input";
import { useFormProps } from "@ab.uitools/ui-library/hooks/useFormProps";
import { type Dispatch, type SetStateAction, useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";

interface Props {
  data?: {
    note?: string;
    number?: string;
  } | null;
  isEdit?: boolean;
  info: IAttachmentInfo | null;
  setIsDisabled: Dispatch<SetStateAction<boolean>>;
  setInfo: Dispatch<SetStateAction<IAttachmentInfo | null>>;
  attachmentType?: AttachmentType | null;
  isAccountAttachment: boolean;
  clientId?: string;
  beneficiaryId?: string;
  setClientId: Dispatch<SetStateAction<string>>;
  getCardHolder: ReturnType<typeof useLazyGetCardHolderQuery>[0];
  getAccountHolder: ReturnType<typeof useLazyGetAccountHolderQuery>[0];
  validateAttachment: ReturnType<typeof useAttachmentValidationMutation>[0];
  hasApiError: boolean;
  attachmentValidation?: {
    hasDuplicateEntry: boolean;
    hasBeneficiaryWithSameEntryClientCode: boolean;
  };
  onDifferentClientError: (value: boolean) => void;
  allowClientChange?: boolean;
  onFormChange?: (isDirty: boolean) => void;
}

export const AttachForm = ({
  data,
  info,
  isEdit,
  setInfo,
  clientId,
  setClientId,
  hasApiError,
  getCardHolder,
  setIsDisabled,
  beneficiaryId,
  attachmentType,
  getAccountHolder,
  validateAttachment,
  isAccountAttachment,
  attachmentValidation,
  onDifferentClientError,
  allowClientChange,
  onFormChange,
}: Props) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { setError, clearErrors, watch, errors } = useFormProps();
  const { clearAlertNotification } = useNotify();

  const values = watch?.();
  const hasChanges = useMemo(() => {
    return data?.number !== trimAll(values?.number) || data?.note !== values?.note;
  }, [data, values]);

  const setNumberError = (error: IError) => {
    const message = t(getErrorMessage(error));
    if (message) {
      setError?.(
        BeneficiaryAttachmentKeys.Number,
        { message }
      );
    } else {
      dispatch(setAlertNotification({
        type: SnackbarAlertTypes.Error,
        message: t(ErrorMessages.GeneralApiError),
      }));
    }
  };

  const onAttachmentValidation = (type: string, id?: string) => {
    if (beneficiaryId && id) {
      setIsDisabled(false);
      return;
    }
    const number = watch?.(BeneficiaryAttachmentKeys.Number);
    const trimNumber = trimAll(number);
    if (data?.number === trimNumber && isEdit) {
      setIsDisabled(hasChanges);
      return;
    }
    validateAttachment({ type, number: trimNumber })
      .unwrap()
      .then((res) => {
        const hasError = res.hasDuplicateEntry || res.hasBeneficiaryWithSameEntryClientCode;
        setIsDisabled(hasError);
        if (res.hasDuplicateEntry) {
          setError?.(
            BeneficiaryAttachmentKeys.Number,
            { message: ErrorMessages.DuplicateBeneficiaryNumber }
          );
        }
        if (res.hasBeneficiaryWithSameEntryClientCode) {
          setError?.(
            BeneficiaryAttachmentKeys.Number,
            { message: ErrorMessages.DuplicateBeneficiaryClientCode }
          );
        }
      })
      .catch((error) => {
        setNumberError(error);
      });
  };

  const handleCheckClientId = (type: string, id?: string) => {
    if (id && clientId && id !== clientId) {
      if (!allowClientChange) {
        setError?.(
          BeneficiaryAttachmentKeys.Number,
          { message: ErrorMessages.DifferentClient }
        );
        onDifferentClientError(true);
        return;
      }
      onDifferentClientError(false);
      onAttachmentValidation(type, clientId);
      return;
    }
    const number = watch?.(BeneficiaryAttachmentKeys.Number);
    if (data?.number !== number) {
      onAttachmentValidation(type, id);
      return;
    }
    onDifferentClientError(false);
    if (id && !clientId) setClientId(id);
    onAttachmentValidation(type, clientId);
  };

  const getInfo = useCallback(
    debounce((val: string) => {
      setInfo(null);
      clearAlertNotification();
      const value = trimAll(val);
      if (attachmentType === AttachmentType.Account &&
        isValidAccountChecksum(value) &&
        isValidAccount(value)
      ) {
        getAccountHolder(value)
          .unwrap()
          .then((res) => {
            clearErrors?.(BeneficiaryAttachmentKeys.Number);
            setInfo({
              holder: res.holder,
              clientId: res.clientId,
              network: AttachmentNetwork.ACCOUNT
            });
            handleCheckClientId(BeneficiaryType.Account, res.clientId);
          })
          .catch((error) => {
            setNumberError(error);
          });
      } else if (attachmentType === AttachmentType.Card && isValidCard(value)) {
        getCardHolder(value)
          .unwrap()
          .then((res) => {
            clearErrors?.(BeneficiaryAttachmentKeys.Number);
            setInfo({
              clientId: res.clientId,
              holder: res.embossedName,
              network: AttachmentNetwork?.[res.network as keyof typeof AttachmentNetwork],
            });
            handleCheckClientId(BeneficiaryType.Card, res.clientId);
          })
          .catch((error) => {
            setNumberError(error);
          });
      }
    }, 700),
    [attachmentType],
  );

  useEffect(() => {
    onFormChange?.(hasChanges);
  }, [hasChanges]);

  useEffect(() => {
    return () => {
      setInfo(null);
    };
  }, []);

  return (
    <>
      <FormField
        name="number"
        className="mb-32"
        errorMessageIcon="infoFilled"
        As={(props) => (
          <Input
            {...props}
            required
            helperText={!errors?.number ? info?.holder : ""}
            handleChange={(_, v) => {
              setIsDisabled(true);
              clearErrors?.(BeneficiaryAttachmentKeys.Number);
              const trimmed = trimAll(v);
              const isValidFormat = isAccountAttachment
                ? isValidAccountChecksum(trimmed) && isValidAccount(trimmed)
                : isValidCard(trimmed);
              if (!trimmed || !isValidFormat) {
                props.onBlur?.();
              }
              getInfo(v);
            }}
            format={!isAccountAttachment ? "#### #### #### ####" : ""}
            currentValue={watch?.(BeneficiaryAttachmentKeys.Number)}
            type="numeric"
            onBlur={() => {
              if (
                !hasApiError &&
                !attachmentValidation?.hasDuplicateEntry &&
                !attachmentValidation?.hasBeneficiaryWithSameEntryClientCode
              ) {
                props.onBlur?.();
              }
            }}
            label={
              isAccountAttachment ?
                t("beneficiary.accountAttchment.number") :
                t("beneficiary.cardAttchment.number")
            }
            placeholder={t("beneficiary.enterNumber")}
          />
        )}
      />
      <FormField
        name="note"
        className="mb-32"
        errorMessageIcon="infoFilled"
        As={(props) => (
          <Input
            {...props}
            maxCount={32}
            maxLength={32}
            label={t("beneficiary.note")}
            placeholder={t("beneficiary.enterNote")}
          />
        )}
      />
    </>
  );
};
